import { useEffect, useState, useCallback } from "react";
import { usePostHog } from "posthog-js/react";
import { View, StyleSheet, Image } from "react-native";
import CustomText from "../components/common/general/CustomText/CustomText";
import { RouteProp, useFocusEffect, useNavigation, useRoute } from "@react-navigation/native";
import { MotiView } from "moti";
import { useGetCourseByIdQuery, useMeQuery } from "../graphql/generated/graphql";
import CustomProgressBar from "../components/common/general/CustomProgressBar/CustomProgressBar";
import JourneyBreadcrumbs from "../components/journey/JourneyBreadcrumbs/JourneyBreadcrumbs";
import Skeleton from "react-loading-skeleton";
import AiTutorCardProgressListContainer from "../containers/school/AiTutorCardProgressListContainer/AiTutorCardProgressListContainer";
import CustomSpacing from "../components/common/layout/CustomSpacing/CustomSpacing";
import { RootStackParamList } from "../navigation/AppNavigator.web";
import { NativeStackNavigationProp } from "@react-navigation/native-stack";
import useResponsiveScreen from "../hooks/useResponsiveScreen";
import DefaultLayout from "../layouts/DefaultLayout.web";
import { useActiveCourse, ResourceType } from "../contexts/ActiveCourseContext";

type CustomModuleStudentViewRouteProp = RouteProp<RootStackParamList, "Custom Module Student">;
type CustomModuleStudentViewNavigationProp = NativeStackNavigationProp<RootStackParamList, "Custom Module Student">;

const CustomModuleStudentView = () => {
  const [totalProgress, setTotalProgress] = useState<number>(0);

  const navigation = useNavigation<CustomModuleStudentViewNavigationProp>();
  const route = useRoute<CustomModuleStudentViewRouteProp>();
  const posthog = usePostHog();
  const { isLarge } = useResponsiveScreen();
  const { setActiveResource } = useActiveCourse();

  const { module_id: moduleId, course_id: courseId, integration_id: integrationId } = route.params;

  const { data: meData } = useMeQuery();
  const userId = meData?.me?.id;

  const {
    data: courseData,
    loading,
    error,
  } = useGetCourseByIdQuery({
    variables: { id: courseId, withModules: true },
    fetchPolicy: "cache-and-network",
  });

  const course = courseData?.getCourseById?.data?.[0];
  const currentModule = course?.modules?.find((m) => m.id === moduleId);

  useFocusEffect(
    useCallback(() => {
      if (currentModule) {
        setActiveResource({ resourceType: ResourceType.MODULE, resourceId: currentModule.id });
      }
      return () => {
        const currentRoute = navigation.getState()?.routes[navigation.getState()?.index];
        const isChildResourceView =
          currentRoute?.name === "Custom Assignment Student" ||
          currentRoute?.name === "Custom Resource Student" ||
          currentRoute?.name === "Custom Course AITutor" ||
          currentRoute?.name === "Project";

        if (!isChildResourceView) {
          setActiveResource(null);
        }
      };
    }, [currentModule, setActiveResource, navigation])
  );

  useEffect(() => {
    if (currentModule && userId) {
      posthog.identify(userId);
      posthog.capture("module_view", {
        module_id: currentModule.id,
        module_title: currentModule.title,
        course_id: courseId,
        course_name: course?.name,
        user_id: userId,
      });
    }
  }, [currentModule, courseId, course?.name, userId, posthog]);

  const handleProgressUpdate = useCallback((progress: number) => setTotalProgress(progress), []);

  const renderModuleHeader = () => {
    if (currentModule?.thumbnail) {
      return (
        <View style={{ flexDirection: isLarge ? "row" : "column", gap: isLarge ? 100 : 16 }}>
          {/* Text column */}
          <View style={[styles.headerTextCol, { width: isLarge ? "50%" : "100%" }]}>
            <View>
              <CustomText text="MODULE" size="m" weight="medium" style={{ marginBottom: 6 }} />
              <CustomText
                text={currentModule.title || "Untitled Module"}
                size="s"
                textType="display"
                weight="ultraBold"
                useTranslationText={false}
              />
              {!!currentModule.description && (
                <CustomText
                  text={currentModule.description}
                  size="l"
                  weight="normal"
                  style={styles.moduleDescription}
                  useTranslationText={false}
                />
              )}
            </View>

            <View style={styles.progressBox}>
              <View style={styles.progressHeader}>
                <CustomText text="Module Progress" size="s" weight="bold" useTranslationText={false} />
                <CustomText
                  text={`${Math.round(totalProgress)}% Complete`}
                  size="s"
                  weight="bold"
                  useTranslationText={false}
                />
              </View>
              <CustomSpacing type="vertical" size="s" />
              <CustomProgressBar
                progress={totalProgress}
                color={totalProgress >= 100 ? "#22C55E" : "#3E68FE"}
                unfilledColor="#EAECF0"
                ariaLabel={`Module progress: ${Math.round(totalProgress)}% complete`}
              />
            </View>
          </View>

          {/* Image column */}
          <View style={{ width: isLarge ? "50%" : "100%" }}>
            <Image
              source={{ uri: currentModule.thumbnail }}
              accessibilityLabel={`${currentModule.title} thumbnail`}
              resizeMode="cover"
              style={styles.headerImage}
            />
          </View>
        </View>
      );
    }

    // No thumbnail
    return (
      <View style={styles.headerNoImage}>
        <View style={{ marginBottom: 0 }}>
          <CustomText
            text={currentModule?.title || "Untitled Module"}
            size="s"
            textType="display"
            weight="ultraBold"
            style={styles.moduleTitle}
            useTranslationText={false}
          />
          {!!currentModule?.description && (
            <CustomText
              text={currentModule.description}
              size="l"
              weight="normal"
              style={styles.moduleDescription}
              useTranslationText={false}
            />
          )}
        </View>

        <CustomSpacing type="vertical" size="l" />

        <View style={styles.progressBox}>
          <View style={styles.progressHeader}>
            <CustomText text="Module Progress" size="s" weight="bold" useTranslationText={false} />
            <CustomText
              text={`${Math.round(totalProgress)}% Complete`}
              size="s"
              weight="bold"
              useTranslationText={false}
            />
          </View>
          <CustomSpacing type="vertical" size="s" />
          <CustomProgressBar
            progress={totalProgress}
            color={totalProgress >= 100 ? "#22C55E" : "#3E68FE"}
            unfilledColor="#EAECF0"
            ariaLabel={`Module progress: ${Math.round(totalProgress)}% complete`}
          />
        </View>
      </View>
    );
  };

  if (loading) {
    return (
      <DefaultLayout pageHasChat={false}>
        <View style={styles.container}>
          <Skeleton height={220} style={{ marginBottom: 20 }} />
          <Skeleton height={40} width="60%" style={{ marginBottom: 10 }} />
          <Skeleton height={20} width="80%" style={{ marginBottom: 20 }} />
          {[1, 2, 3].map((idx) => (
            <Skeleton key={idx} height={80} style={{ marginBottom: 10 }} />
          ))}
        </View>
      </DefaultLayout>
    );
  }

  if (error || !course || !currentModule) {
    return (
      <DefaultLayout>
        <View style={styles.container}>
          <CustomText text="Module not found or error loading data" size="m" weight="medium" />
        </View>
      </DefaultLayout>
    );
  }

  return (
    <DefaultLayout pageHasChat={false}>
      <View style={styles.container}>
        <JourneyBreadcrumbs
          onPress={() =>
            navigation.navigate("Custom Course Student", {
              course_id: courseId,
              integration_id: integrationId,
            })
          }
          firstItem={course?.name || "Course"}
          secondItem={currentModule?.title || "Module"}
        />

        {renderModuleHeader()}

        <CustomSpacing type="vertical" size="xl" />

        <MotiView
          from={{ opacity: 0, translateY: 20 }}
          animate={{ opacity: 1, translateY: 0 }}
          delay={300}
          transition={{
            opacity: { type: "timing", duration: 600 },
            translateY: { type: "timing", duration: 600 },
          }}
          style={styles.contentContainer}>
          <AiTutorCardProgressListContainer
            integrationId={integrationId}
            courseId={courseId}
            moduleId={moduleId}
            onProgressUpdate={handleProgressUpdate}
          />
        </MotiView>
      </View>
    </DefaultLayout>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
    paddingHorizontal: 20,
    paddingTop: 20,
    width: "100%",
    maxWidth: 1200,
    alignSelf: "center",
  },
  headerTextCol: {
    flex: 1,
    justifyContent: "space-between",
    paddingVertical: 16,
  },

  headerImage: {
    width: "100%",
    height: 260,
    borderRadius: 12,
  },
  moduleTitle: {
    color: "#1F2937",
    lineHeight: 40,
  },
  moduleDescription: {
    color: "#101828",
    lineHeight: 28,
    marginTop: 8,
  },
  progressBox: {
    backgroundColor: "#F9FAFB",
    borderRadius: 12,
    padding: 20,
    marginTop: 16,
  },
  progressHeader: {
    flexDirection: "row",
    justifyContent: "space-between",
    alignItems: "center",
  },
  headerNoImage: { paddingVertical: 0 },

  contentContainer: { flex: 1 },
});

export default CustomModuleStudentView;
